// !! الخطوة 2: إضافة هذا السطر
import React from 'react'; 
import { toast } from 'react-hot-toast';
import { useState, useEffect, useCallback  } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  ColumnDef,
  SortingState,
  PaginationState,
 
} from '@tanstack/react-table';
import { useDebounce } from 'use-debounce';

// --- أنواع البيانات (Types) ---

// نوع البيانات التي نتوقعها من الـ API عند طلب صفحة
export interface PagedResult<T> {
  items: T[];
  totalItems: number;
  totalPages: number;
  currentPage: number;
  
}

// نوع البيانات التي نرسلها إلى الـ API في كل طلب
export interface TableParams {
  page: number;
  pageSize: number;
  pageIndex: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  search?: string;
}

// نوع الـ props التي سيستقبلها الـ hook
interface UseServerSideTableProps<T extends object> {
  columns: ColumnDef<T>[];
  fetchData: (params: TableParams) => Promise<PagedResult<T>>;
}

/**
 * Hook مخصص لإدارة الجداول التي تعتمد على الخادم باستخدام TanStack Table (v8)
 */
export const useServerSideTable = <T extends object>({
  columns,
  fetchData,
}: UseServerSideTableProps<T>) => {
  // --- إدارة الحالة (State Management) ---
  const [data, setData] = useState<T[]>([]);
  const [pageCount, setPageCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm] = useDebounce(searchTerm, 500);
   const [refreshCounter, setRefreshCounter] = useState(0);
  // حالة الترقيم والفرز الخاصة بـ TanStack Table
  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [sorting, setSorting] = useState<SortingState>([]);

  // --- جلب البيانات (Data Fetching) ---
 const fetchDataCallback = useCallback(async () => {
    setLoading(true);
    const sortRule = sorting[0];
    const params: TableParams = {
      page: pageIndex,
      pageSize,
      pageIndex,
      sortBy: sortRule ? sortRule.id : undefined,
      sortOrder: sortRule ? (sortRule.desc ? 'desc' : 'asc') : undefined,
      search: debouncedSearchTerm,
    };
    try {
      const result = await fetchData(params);
      setData(result.items);
      setPageCount(result.totalPages); // تأكد من أن الخادم يرسل totalPages
    } catch (error) {
      console.error('Failed to fetch table data:', error);
      toast.error("فشل في جلب بيانات الجدول."); // إعلام المستخدم بالخطأ
      setData([]);
      setPageCount(0);
    } finally {
      setLoading(false);
    }
  }, [pageIndex, pageSize, sorting, debouncedSearchTerm, fetchData]);

  // 4. تعديل useEffect ليعتمد على العداد
  useEffect(() => {
    fetchDataCallback();
  }, [fetchDataCallback, refreshCounter]); 
  // --- إعداد الجدول (Table Instance) ---

  const table = useReactTable({
    data,
    columns,
    pageCount,
    state: {
      pagination: { pageIndex, pageSize },
      sorting,
    },
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: true,
    manualSorting: true,
  });

  // --- مكونات واجهة المستخدم المساعدة (UI Components) ---
  // بما أن هذا الملف أصبح .tsx، هذا الكود سيعمل الآن
  /*const Header = () => (
    <div style={{ marginBottom: '1rem', maxWidth: '400px' }}>
      <input
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        placeholder="ابحث في الجدول..."
        style={{ width: '100%', padding: '0.8rem', borderRadius: '6px', border: '1px solid #ccc' }}
      />
    </div>
  );*/

  const Pagination = () => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1rem', flexWrap: 'wrap', gap: '1rem' }}>
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <button onClick={() => table.setPageIndex(0)} disabled={!table.getCanPreviousPage()}>
          {'<<'}
        </button>
        <button onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()}>
          {'<'}
        </button>
        <button onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>
          {'>'}
        </button>
        <button onClick={() => table.setPageIndex(table.getPageCount() - 1)} disabled={!table.getCanNextPage()}>
          {'>>'}
        </button>
      </div>
      <span>
        صفحة{' '}
        <strong>
          {table.getState().pagination.pageIndex + 1} من {table.getPageCount() || 1}
        </strong>
      </span>
      <select
        value={table.getState().pagination.pageSize}
        onChange={(e) => {
          table.setPageSize(Number(e.target.value));
        }}
      >
        {[10, 20, 30, 40, 50].map((size) => (
          <option key={size} value={size}>
            إظهار {size}
          </option>
        ))}
      </select>
    </div>
  );
 // 5. تعريف دالة التحديث
  const refreshData = () => {
    setRefreshCounter(prevCounter => prevCounter + 1);
  };
  // --- القيمة المعادة من الـ Hook ---
  return {
    table,
    //Header,
    Pagination,
    isLoading: loading,
   refreshData,
   // أضف هذه الخصائص الجديدة
    searchTerm,
    setSearchTerm,
  };
};
